import React, { useEffect, useState, useCallback } from "react";
import { getDeviceCatalog, getDevices, addDevice, updateDevice, deleteDevice, importDevices } from "../lib/api";
import { BulkImportDialog } from "./BulkImportDialog";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Cpu, Plus, Trash2, CheckCircle2, AlertTriangle, Ban, HelpCircle, ShieldAlert, FileUp } from "lucide-react";

const VERDICT = {
  ok: { label: "Supported", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25", icon: CheckCircle2 },
  warning: { label: "Review", cls: "bg-amber-500/10 text-amber-400 border-amber-500/25", icon: AlertTriangle },
  blocker: { label: "Not supported", cls: "bg-red-500/10 text-red-400 border-red-500/25", icon: Ban },
  unknown: { label: "Unknown", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25", icon: HelpCircle },
};

export const DeviceInventory = ({ siteId, track }) => {
  const [catalog, setCatalog] = useState([]);
  const [reviewed, setReviewed] = useState("");
  const [data, setData] = useState(null);
  const [model, setModel] = useState("");
  const [firmware, setFirmware] = useState("");
  const [qty, setQty] = useState("1");
  const [adding, setAdding] = useState(false);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => { getDeviceCatalog().then((d) => { setCatalog(d.models || []); setReviewed(d.reviewed); }).catch(() => {}); }, []);

  const load = useCallback(() => {
    if (!siteId) return;
    getDevices(siteId, track).then(setData).catch(() => setData({ devices: [], summary: { status: "empty", counts: {} } }));
  }, [siteId, track]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!model.trim()) return;
    setAdding(true);
    try {
      await addDevice(siteId, { model: model.trim(), firmware: firmware.trim(), qty: parseInt(qty, 10) || 1 }, track);
      setModel(""); setFirmware(""); setQty("1");
      load();
    } catch { toast.error("Could not add device"); }
    setAdding(false);
  };

  const patchFirmware = async (id, val, cur) => {
    if (val === (cur || "")) return;
    try { await updateDevice(siteId, id, { firmware: val }, track); load(); } catch { toast.error("Save failed"); }
  };
  const patchQty = async (id, val) => {
    try { await updateDevice(siteId, id, { qty: parseInt(val, 10) || 1 }, track); load(); } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { try { await deleteDevice(siteId, id); load(); } catch { toast.error("Failed"); } };

  const summary = data?.summary || { status: "empty", counts: {} };
  const c = summary.counts || {};
  const banner = {
    empty: { cls: "border-zinc-800 bg-zinc-900/40 text-zinc-400", text: "No equipment added yet. Add the phones/endpoints at this site to check them against the target platform." },
    ok: { cls: "border-emerald-600/30 bg-emerald-600/10 text-emerald-300", text: "All equipment is supported on this track — cutover is not blocked by device compatibility." },
    warning: { cls: "border-amber-500/30 bg-amber-500/10 text-amber-200", text: `${(c.warning || 0) + (c.unknown || 0)} item(s) need review (End-of-Life, firmware, or not in catalog). Cutover is allowed but verify these.` },
    blocker: { cls: "border-red-600/40 bg-red-600/10 text-red-200", text: `${c.blocker || 0} unsupported device model(s) — cutover is BLOCKED until these are resolved or replaced.` },
  }[summary.status];

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden" data-testid="device-inventory">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Cpu className="h-4 w-4 text-blue-400" />
        <span className="text-sm font-bold text-white">Device Compatibility</span>
        <span className="text-[11px] text-zinc-500">· {track === "cucm15" ? "CUCM 15" : "Webex Calling"} readiness gate</span>
        <button
          onClick={() => setShowImport(true)}
          data-testid="device-import-btn"
          className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
        >
          <FileUp className="h-3.5 w-3.5" /> Import from CUCM
        </button>
        {summary.status === "blocker" && <ShieldAlert className="h-4 w-4 text-red-400" title="Cutover blocked" />}
      </div>

      <BulkImportDialog
        open={showImport} onOpenChange={setShowImport} testid="device-import"
        title="Import devices from a CUCM / Webex export"
        description="Upload or paste a CUCM phone export (BAT / RTMT / AXL SQL) — one row per phone. We auto-aggregate by model, keep the lowest firmware, and flag each against the compatibility matrix."
        columns={[
          { key: "model", synonyms: ["device type", "devicetype", "phone model", "phonemodel", "product", "product type", "producttype", "type", "model name", "modelname"] },
          { key: "firmware", synonyms: ["load", "active load", "activeload", "active load id", "activeloadid", "firmware load", "load information", "loadinformation", "current load", "fw", "load id", "loadid"] },
          { key: "qty", synonyms: ["quantity", "count", "units"] },
        ]}
        sampleCsv={"model,firmware,qty\nCisco 8845,12.8(1),40\nCisco 7841,11.7(1),25\nCisco 3905,9.4(1),12"}
        sampleFilename="cucm_device_export.csv"
        onImport={(rows) => importDevices(siteId, rows, track)}
        onDone={load}
      />

      <div className="p-4">
        <div data-testid="device-summary-banner" className={`flex items-start gap-2 text-[12px] rounded-md border px-3 py-2 ${banner.cls}`}>
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" /> {banner.text}
        </div>

        {/* Add form */}
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="device-add-form">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="device-model-select" className="h-9 bg-zinc-950 border-zinc-800 text-[13px] flex-1"><SelectValue placeholder="Select a Cisco model…" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
              {catalog.map((m) => <SelectItem key={m.model} value={m.model}>{m.model} · {m.series}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="device-firmware-input" value={firmware} onChange={(e) => setFirmware(e.target.value)} placeholder="Firmware (opt.)" className="h-9 bg-zinc-950 border-zinc-800 text-[13px] sm:w-36" />
          <Input data-testid="device-qty-input" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 bg-zinc-950 border-zinc-800 text-[13px] sm:w-20" />
          <button type="submit" disabled={adding || !model} data-testid="device-add-btn" className="h-9 px-4 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-[13px] font-semibold flex items-center justify-center gap-1.5">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>

        {/* List */}
        <div className="mt-4 space-y-1.5">
          {(data?.devices || []).map((d) => {
            const v = VERDICT[d.eval.verdict] || VERDICT.unknown;
            const Icon = v.icon;
            return (
              <div key={d.id} data-testid={`device-row-${d.id}`} className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon className={`h-4 w-4 shrink-0 ${v.cls.split(" ")[1]}`} />
                  <span className="text-[13px] text-white font-medium">{d.model}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${v.cls}`} data-testid={`device-verdict-${d.id}`}>{v.label}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <Input data-testid={`device-firmware-${d.id}`} defaultValue={d.firmware || ""} onBlur={(e) => patchFirmware(d.id, e.target.value.trim(), d.firmware)} placeholder="fw" className="h-7 w-24 bg-zinc-900 border-zinc-800 text-[11px]" />
                    <Input data-testid={`device-qty-${d.id}`} type="number" min="1" defaultValue={d.qty} onBlur={(e) => patchQty(d.id, e.target.value)} className="h-7 w-16 bg-zinc-900 border-zinc-800 text-[11px]" />
                    <button onClick={() => remove(d.id)} data-testid={`device-delete-${d.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                {d.eval.reasons?.length > 0 && (
                  <div className="mt-1.5 pl-6 text-[11px] text-zinc-400">
                    {d.eval.reasons[0]}
                    {d.eval.recommended_firmware && <span className="text-zinc-600"> · Recommended firmware ≥ {d.eval.recommended_firmware}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {reviewed && (
          <p className="mt-3 text-[10px] text-zinc-600">
            Curated matrix (last reviewed {reviewed}). Advisory only — always confirm against Cisco EoX / release notes before cutover.
          </p>
        )}
      </div>
    </div>
  );
};
